/**
 * Pure formatting helpers for coaching pricing plans (see migration 0018). No
 * network/state — just turns a plan's integer-cents price + structured
 * inclusions into display strings for the builder, picker, and roster.
 *
 * Money is always integer cents (never a float); these are the ONLY place plan
 * money is rendered, mirroring how describeBilling owns billing-status copy.
 * Types are structural so each app's own CoachingPlan flows through unchanged.
 */

export type BillingInterval = 'month' | 'week' | 'session' | 'year';

export interface PlanInclusions {
  sessionsPerWeek?: number;
  sessionLengthMin?: number;
  sessionsTotal?: number;
}

export interface PlanMoney {
  priceCents: number;
  billingInterval: BillingInterval;
  currency?: string;
}

const SYMBOLS: Record<string, string> = { usd: '$', eur: '€', gbp: '£', cad: '$', aud: '$', nzd: '$' };

/** "$500", "$49.99" — whole dollars drop the decimals; fractional keeps two. */
export const formatMoney = (cents: number, currency = 'usd'): string => {
  const dollars = (cents ?? 0) / 100;
  const amount = Number.isInteger(dollars) ? String(dollars) : dollars.toFixed(2);
  const symbol = SYMBOLS[currency.toLowerCase()];
  return symbol ? `${symbol}${amount}` : `${currency.toUpperCase()} ${amount}`;
};

/** Short cadence suffix for a price line: month→mo, week→wk, year→yr, session→session. */
export const intervalSuffix = (interval: BillingInterval): string =>
  interval === 'month' ? 'mo' : interval === 'week' ? 'wk' : interval === 'year' ? 'yr' : 'session';

/** Headline price for a plan, e.g. "$500/mo". */
export const planPriceLabel = (plan: PlanMoney): string =>
  `${formatMoney(plan.priceCents, plan.currency ?? 'usd')}/${intervalSuffix(plan.billingInterval)}`;

/** "3 sessions/wk · 60 min each · 24 total" from the structured inclusions (blanks omitted). */
export const inclusionsSummary = (inclusions?: PlanInclusions | null): string => {
  if (!inclusions) return '';
  const parts: string[] = [];
  if (inclusions.sessionsPerWeek) parts.push(`${inclusions.sessionsPerWeek} session${inclusions.sessionsPerWeek === 1 ? '' : 's'}/wk`);
  if (inclusions.sessionLengthMin) parts.push(`${inclusions.sessionLengthMin} min each`);
  if (inclusions.sessionsTotal) parts.push(`${inclusions.sessionsTotal} total`);
  return parts.join(' · ');
};

/** One-line summary for pickers/rosters: "$500/mo · 3 sessions/wk · 60 min each". */
export const planSummary = (plan: PlanMoney & { inclusions?: PlanInclusions | null }): string => {
  const inc = inclusionsSummary(plan.inclusions);
  return inc ? `${planPriceLabel(plan)} · ${inc}` : planPriceLabel(plan);
};
